// Tutorial guiado: um "treinador" que acompanha a partida de verdade (sala
// "tutorial") e ensina um passo de cada vez, destacando a parte da tela certa.
// Cada passo termina quando a pessoa faz o que ele pede (ou depois de uma rodada).
import type { GameStore } from "./game/store";
import type { PlayerView } from "./game/types";
import { t, type MsgKey } from "./i18n";
import { spriteHtml } from "./sprites";
import { esc } from "./ui";

type Me = NonNullable<PlayerView["me"]>;
interface Ctx { v: PlayerView; me: Me; startedRound: string; round: string }
interface Step {
  id: string;
  /** seletor do que destacar (opcional) */
  target?: string;
  /** só aparece quando isto for verdade (ex.: ganhou um componente) */
  when?: (c: Ctx) => boolean;
  done: (c: Ctx) => boolean;
}

const units = (me: Me) => [...me.board, ...me.bench].filter(Boolean);
const roundKey = (v: PlayerView) => `${v.stage}-${v.round}`;
const passedRound = (c: Ctx) => c.round !== c.startedRound;

export const COACH_STEPS: Step[] = [
  { id: "shop", target: "[data-shop]", done: (c) => units(c.me).length > 0 },
  { id: "place", target: "[data-board]", done: (c) => c.me.board.some(Boolean) },
  { id: "fight", target: "[data-ready]", done: (c) => c.v.roomPhase === "combat" },
  { id: "watch", done: (c) => c.v.roomPhase === "planning" && passedRound(c) },
  { id: "combine", target: "[data-shop]", done: (c) => units(c.me).some((u) => u!.star >= 2) || (passedRound(c) && c.v.roomPhase === "planning") },
  { id: "traits", target: ".game__traits", done: (c) => passedRound(c) && c.v.roomPhase === "planning" },
  { id: "items", target: "[data-components]", when: (c) => c.me.components.length > 0, done: (c) => c.me.components.length === 0 },
  { id: "xp", target: "[data-xpbtn]", done: (c) => c.me.level >= 4 || (passedRound(c) && c.v.roomPhase === "planning") },
  { id: "interest", target: "[data-gold]", done: (c) => passedRound(c) && c.v.roomPhase === "planning" },
  { id: "end", done: () => false },
];

/** Liga o treinador numa partida de tutorial. Devolve a função que desliga. */
export function startCoach(store: GameStore, root: HTMLElement): () => void {
  let i = 0;
  let startedRound = "";
  const box = document.createElement("div");
  box.className = "coach";
  box.setAttribute("role", "status");
  box.setAttribute("aria-live", "polite");
  root.appendChild(box);
  let lastTarget: Element | null = null;

  const highlight = (sel?: string) => {
    lastTarget?.classList.remove("coach-target");
    lastTarget = sel ? root.querySelector(sel) : null;
    lastTarget?.classList.add("coach-target");
  };

  const show = (step: Step) => {
    box.innerHTML = `${spriteHtml(25, 56)}<div><b>${t("tut.coach")}</b><p>${esc(t(`tut.${step.id}` as MsgKey))}</p></div>
      <button class="bw-btn bw-btn--square coach-skip" data-coach-skip aria-label="${t("tut.next")}">›</button>`;
    highlight(step.target);
  };

  const tick = () => {
    const v = store.view;
    if (!v?.me) return;
    const c: Ctx = { v, me: v.me, startedRound, round: roundKey(v) };
    let step = COACH_STEPS[i]!;
    // passos que dependem de algo (itens) esperam aparecer; os outros seguem a ordem
    while (step.when && !step.when(c) && i < COACH_STEPS.length - 1) step = COACH_STEPS[++i]!;
    if (step.done(c)) {
      i = Math.min(i + 1, COACH_STEPS.length - 1);
      startedRound = roundKey(v);
      step = COACH_STEPS[i]!;
    }
    if (box.dataset.step !== step.id) {
      box.dataset.step = step.id;
      if (!startedRound) startedRound = roundKey(v);
      show(step);
    } else if (step.target && !lastTarget?.isConnected) {
      highlight(step.target); // a tela foi redesenhada
    }
  };

  box.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement).closest("[data-coach-skip]")) return;
    i = Math.min(i + 1, COACH_STEPS.length - 1);
    startedRound = store.view ? roundKey(store.view) : "";
    box.dataset.step = "";
    tick();
  });
  const unsub = store.subscribe(tick);
  const timer = window.setInterval(tick, 500);
  tick();
  return () => {
    unsub();
    clearInterval(timer);
    highlight();
    box.remove();
  };
}
